import { memo, ReactElement, useMemo } from 'react';
import { Divider, Grid } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import PmiTextField from 'shell/components/shared/text-field';
import { PublicationVM } from 'civil-status/view-models/step-form';
import { initialFilterPublication } from 'civil-status/constants/initial-values-step';
import PmiTooltipError from 'shell/components/shared/tooltip-error';

interface FormFilterComponentProps {
  values?: PublicationVM;
  onChange: (key: keyof PublicationVM, value: PublicationVM[keyof PublicationVM]) => void;
  onChanges: (values: PublicationVM) => void;
  disabled?: boolean;
  onSearch: () => void;
}

const FormFilterComponent = ({
  values,
  onChange,
  onChanges,
  disabled,
  onSearch,
}: FormFilterComponentProps): ReactElement => {
  const disabledFirstResearch: boolean = useMemo(() => {
    return Boolean(values?.yearSurname || values?.surnameGroom || values?.surnameWife);
  }, [values]);

  const disabledSecondResearch: boolean = useMemo(() => {
    // abilitare se completa una delle due ricerche
    return Boolean(
      values?.year || values?.number || values?.part || values?.part || values?.register
    );
  }, [values]);

  const enableResearch: boolean = useMemo(() => {
    return (
      Boolean(values?.year && values?.number && values?.part && values?.register) ||
      Boolean(values?.yearSurname && values?.surnameGroom && values?.surnameWife)
    );
  }, [values]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={2}>
        <PmiTextField
          id="year"
          label={`Anno ${!disabledFirstResearch ? '*' : ''}`}
          aria-label="Anno"
          fullWidth
          onChange={(event) => onChange('year', event.target.value)}
          value={values?.year}
          type="number"
          inputProps={{
            maxLength: '4',
          }}
          disabled={disabledFirstResearch || disabled}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          id="number"
          label={`Numero ${!disabledFirstResearch ? '*' : ''}`}
          aria-label="Numero"
          fullWidth
          onChange={(event) => onChange('number', event.target.value)}
          value={values?.number}
          disabled={disabledFirstResearch || disabled}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          id="part"
          label={`Parte ${!disabledFirstResearch ? '*' : ''}`}
          aria-label="Parte"
          fullWidth
          onChange={(event) => onChange('part', event.target.value)}
          value={values?.part}
          inputProps={{
            maxLength: '1',
          }}
          disabled={disabledFirstResearch || disabled}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          id="serie"
          label="Serie"
          aria-label="Serie"
          fullWidth
          onChange={(event) => onChange('serie', event.target.value)}
          value={values?.serie}
          disabled={disabledFirstResearch || disabled}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          id="register"
          label={`Registro ${!disabledFirstResearch ? '*' : ''}`}
          aria-label="Registro"
          fullWidth
          onChange={(event) => onChange('register', event.target.value)}
          value={values?.register}
          disabled={disabledFirstResearch || disabled}
          inputProps={{
            maxLength: '2',
          }}
          type="number"
        />
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12} md={2}>
        <PmiTextField
          id="yearSurname"
          label={`Anno ${!disabledSecondResearch ? '*' : ''}`}
          aria-label="Anno"
          fullWidth
          onChange={(event) => onChange('yearSurname', event.target.value)}
          value={values?.yearSurname}
          type="number"
          inputProps={{
            maxLength: '4',
          }}
          disabled={disabledSecondResearch || disabled}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <PmiTextField
          id="surnameGroom"
          label={`Cognome sposo ${!disabledSecondResearch ? '*' : ''}`}
          aria-label="Cognome sposo"
          fullWidth
          onChange={(event) => onChange('surnameGroom', event.target.value)}
          value={values?.surnameGroom}
          disabled={disabledSecondResearch || disabled}
        />
      </Grid>
      <Grid item xs={12} md={5}>
        <PmiTextField
          id="surnameWife"
          label={`Cognome sposa ${!disabledSecondResearch ? '*' : ''}`}
          aria-label="Cognome sposa"
          fullWidth
          onChange={(event) => onChange('surnameWife', event.target.value)}
          value={values?.surnameWife}
          disabled={disabledSecondResearch || disabled}
        />
      </Grid>
      <Grid item xs={12} container spacing={2} display="flex" justifyContent="center">
        <Grid item xs={12} md={3} style={{ display: 'flex', alignItems: 'center' }}>
          <PmiTooltipError show={!enableResearch} message="Completare una delle due ricerche">
            <span>
              <PmiButton
                color="cdmBrandThird"
                variant="outlined"
                onClick={onSearch}
                disabled={!enableResearch || disabled}
              >
                Cerca
              </PmiButton>
            </span>
          </PmiTooltipError>
        </Grid>
        <Grid item xs={12} md={3} style={{ display: 'flex', alignItems: 'center' }}>
          <PmiButton
            variant="outlined"
            color="cdmBrand"
            onClick={() => onChanges(initialFilterPublication)}
            disabled={disabled}
          >
            Pulisci
          </PmiButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

FormFilterComponent.displayName = 'Filter Publication - Form component';

const FormFilter = memo(FormFilterComponent);

export default FormFilter;
